﻿import type { SaleReceiptPayload } from '@vuelto/print';
import type { SaleDetail, SaleDetailItem } from '../../sales/domain/salesTypes';
import { displayPaymentMethodLabel } from '../../sales/infra/salesPaymentLabels';
import {
  formatSaleDisplayRef,
  formatSaleFulfillmentLabel,
  formatSaleOrderRefLabel,
  formatSaleReceiptFooter,
} from '../../sales/application/salePresentation';
import { summarizeSaleDetailLines } from '../../sales/application/saleDetailPromotionPresentation';
import type { CartItem } from '../../pos/store/usePosStore';
import type { PosDraftPayment } from '../../pos/domain/posTypes';
import { checkoutChangeAmount } from '../../pos/application/posCheckoutMoney';
import { derivePosCheckoutTotals } from '../../pos/application/posScreen.utils';
import { toPaymentAmountNumber } from '../../pos/application/posCheckoutTotals';
import type { CartEvaluationResult } from '../../promotions/domain/promotionTypes';
import { buildFiscalPendingInternalReceiptLines } from '../../fiscal/domain-api';
import { formatMoney } from '../../shared/format/public-api';
import { DEFAULT_MONEY_SCALE, roundMoney } from '@shared/money';
import type { SaleDetailLineSummaryRow } from '../../sales/application/saleDetailPromotionPresentation';

export type SaleReceiptBranding = {
  branchName: string;
  organizationName?: string | null;
};

/** Pie del ticket interno cuando la venta no es comprobante fiscal. */
/** Acento en **i** (U+00ED) — CP850 0xa1 en agente. */
export const NON_FISCAL_RECEIPT_DISCLAIMER = 'No v\u0061l\u00eddo como factura';
/** @deprecated Usar `buildFiscalPendingReceiptFooterNotes` — pie multi-línea para el cliente. */
export const FISCAL_PENDING_RECEIPT_NOTE = 'Factura en emisión — se imprime desde Ventas';

/** Pie del ticket interno cuando la venta es fiscal pero todavía no hay C.A.E. */
export function buildFiscalPendingReceiptFooterNotes(params?: {
  /** Saldo a cuenta (fiado) pendiente en esta venta. */
  accountPendingAmount?: number;
}): string[] {
  const pending = params?.accountPendingAmount;
  const accountLabel =
    pending != null && pending > 0 ? formatMoney(pending) : null;
  return buildFiscalPendingInternalReceiptLines(accountLabel);
}

function formatItemOptionsLabel(item: SaleDetailItem): string | null {
  const opts = item.options;
  if (!opts?.length) return null;
  return opts
    .map((o) => {
      const qty = o.qty > 1 ? `${o.qty}× ` : '';
      return `${qty}${o.choiceName}`;
    })
    .join(', ');
}

/**
 * Handoff QSR del ticket: `isPedido` prende copy de pedido (Pedido #N,
 * fulfillment, pie de mostrador). Sin él la venta es mostrador retail:
 * `Venta #N` + pie genérico. El flag `kitchenTickets` cubre la venta QSR
 * de mostrador (pickup sin nombre, indistinguible del default en datos).
 * Mesa y delivery prenden por dato real. `Retiro:` se imprime siempre que
 * venga nombre — es dato, no copy de cocina (kiosco con retiro online).
 */
function buildHandoffReceiptFields(args: {
  saleId: string;
  counterNumber?: number | null;
  pickupName?: string | null;
  fulfillmentType?: string | null;
  kitchenTickets?: boolean;
  hasTable?: boolean;
}) {
  const fulfillmentType = args.fulfillmentType ?? null;
  const pickupName = args.pickupName?.trim() || null;
  const isPedido = Boolean(
    args.kitchenTickets ||
      args.hasTable ||
      fulfillmentType === 'dine_in' ||
      fulfillmentType === 'delivery',
  );
  return {
    saleRef: formatSaleDisplayRef({
      saleId: args.saleId,
      counterNumber: args.counterNumber,
    }),
    orderRefLabel: formatSaleOrderRefLabel({
      saleId: args.saleId,
      counterNumber: args.counterNumber,
      variant: isPedido ? 'pedido' : 'venta',
    }),
    pickupName,
    fulfillmentLabel: isPedido ? formatSaleFulfillmentLabel(fulfillmentType) : null,
    footerMessage: formatSaleReceiptFooter({
      counterNumber: args.counterNumber,
      fulfillmentType,
      isPedido,
    }),
  };
}

function receiptPaymentsFromDrafts(
  drafts: PosDraftPayment[],
  paymentMethodNames: Record<string, string>,
) {
  return drafts
    .map((draft) => ({
      methodLabel: displayPaymentMethodLabel(draft.methodId, paymentMethodNames),
      amount: toPaymentAmountNumber(draft.amount),
    }))
    .filter((p) => p.amount > 0);
}

function receiptPaymentsFromDetail(
  detail: SaleDetail,
  paymentMethodNames: Record<string, string>,
) {
  return detail.payments
    .map((p) => ({
      methodLabel: displayPaymentMethodLabel(p.methodId, paymentMethodNames),
      amount: p.amount,
    }))
    .filter((p) => p.amount > 0);
}

/**
 * Ítems del ticket en neto por línea; el descuento impreso solo incluye promos de
 * carrito (no repetir lo ya descontado en cada renglón).
 */
function receiptTotalsFromNetLines(params: {
  lineSummaries: SaleDetailLineSummaryRow[];
  merchandiseTotal: number;
  tipAmount: number;
}): {
  subtotal: number;
  discountTotal: number | undefined;
  total: number;
} {
  const tipAmount = Math.max(0, Number(params.tipAmount ?? 0) || 0);
  const linesNetSubtotal = roundMoney(
    params.lineSummaries.reduce((acc, row) => acc + row.netTotal, 0),
    DEFAULT_MONEY_SCALE,
  );
  const merchandiseTotal = roundMoney(params.merchandiseTotal, DEFAULT_MONEY_SCALE);
  const cartWideDiscount = roundMoney(
    Math.max(0, linesNetSubtotal - merchandiseTotal),
    DEFAULT_MONEY_SCALE,
  );
  return {
    subtotal: linesNetSubtotal,
    discountTotal: cartWideDiscount > 0 ? cartWideDiscount : undefined,
    total: roundMoney(merchandiseTotal + tipAmount, DEFAULT_MONEY_SCALE),
  };
}

function cartOptionsLabel(item: CartItem): string | null {
  const opts = item.menuOptions;
  if (!opts?.length) return null;
  return opts
    .map((o) => {
      const qty = o.qty > 1 ? `${o.qty}× ` : '';
      return `${qty}${o.choiceName}`;
    })
    .join(', ');
}

export function mapSaleDetailToReceiptPayload(args: {
  detail: SaleDetail;
  paymentMethodNames: Record<string, string>;
  branding: SaleReceiptBranding;
  diningTableName?: string | null;
  /** Capability `kitchen_tickets` de la org — prende copy handoff en mostrador. */
  kitchenTickets?: boolean;
  /** Leyendas extra bajo el pie (ej. "Factura en emisión…"). */
  footerNotes?: string[];
  trackingUrl?: string | null;
  widthMm?: SaleReceiptPayload['widthMm'];
}): SaleReceiptPayload {
  const { detail, paymentMethodNames, branding, diningTableName, widthMm } = args;
  const tipAmount = Math.max(0, Number(detail.tipAmount ?? 0) || 0);
  const lineSummaries = summarizeSaleDetailLines(detail.items);
  const receiptTotals = receiptTotalsFromNetLines({
    lineSummaries,
    merchandiseTotal: Number(detail.total ?? 0) - tipAmount,
    tipAmount,
  });
  const handoff = buildHandoffReceiptFields({
    saleId: detail.id,
    counterNumber: detail.counterNumber,
    pickupName: detail.pickupName,
    fulfillmentType: detail.fulfillmentType,
    kitchenTickets: args.kitchenTickets,
    hasTable: Boolean(diningTableName?.trim()),
  });

  return {
    saleId: detail.id,
    saleRef: handoff.saleRef,
    orderRefLabel: handoff.orderRefLabel,
    pickupName: handoff.pickupName,
    fulfillmentLabel: handoff.fulfillmentLabel,
    footerMessage: handoff.footerMessage,
    branchName: branding.branchName,
    organizationName: branding.organizationName,
    createdAt: detail.createdAt,
    lines: detail.items.map((item, idx) => {
      const summary = lineSummaries[idx];
      return {
        name: item.label ?? item.productId,
        qty: item.quantity,
        unitPrice: item.price,
        lineTotal: summary?.netTotal ?? item.price * item.quantity,
        optionsLabel: formatItemOptionsLabel(item),
        lineNote: item.lineNote,
        sku: item.sku,
      };
    }),
    subtotal: receiptTotals.subtotal,
    discountTotal: receiptTotals.discountTotal,
    tipAmount: tipAmount > 0 ? tipAmount : undefined,
    total: receiptTotals.total,
    payments: receiptPaymentsFromDetail(detail, paymentMethodNames),
    cashierDisplayName: detail.cashierDisplayName,
    customerName: detail.fiadoCustomerName,
    diningTableName: diningTableName ?? undefined,
    voided: detail.status === 'voided',
    trackingUrl: args.trackingUrl ?? null,
    // Venta no fiscal de una org con ARCA configurado: leyenda legal en el pie.
    footerNotes:
      args.footerNotes ??
      (detail.fiscalMode === 'non_fiscal' && detail.emitterCuit
        ? [NON_FISCAL_RECEIPT_DISCLAIMER]
        : undefined),
    widthMm,
  };
}

export function mapPosCheckoutToReceiptPayload(args: {
  saleId: string;
  branchName: string;
  organizationName?: string | null;
  cart: CartItem[];
  paymentDrafts: PosDraftPayment[];
  paymentMethodNames: Record<string, string>;
  cartEvaluation?: CartEvaluationResult | null;
  tipAmount?: number;
  cashierDisplayName?: string | null;
  customerName?: string | null;
  diningTableName?: string | null;
  counterNumber?: number | null;
  pickupName?: string | null;
  fulfillmentType?: string | null;
  /** Capability `kitchen_tickets` de la org — prende copy handoff en mostrador. */
  kitchenTickets?: boolean;
  createdAt?: string;
  /** Leyendas extra bajo el pie (ej. disclaimer "no válido como factura"). */
  footerNotes?: string[];
  trackingUrl?: string | null;
  widthMm?: SaleReceiptPayload['widthMm'];
}): SaleReceiptPayload {
  const totals = derivePosCheckoutTotals(args.cart, args.paymentDrafts, args.cartEvaluation);
  const tipAmount = Math.max(0, Number(args.tipAmount ?? 0) || 0);
  const evaluatedByLineId = args.cartEvaluation
    ? new Map(args.cartEvaluation.lines.map((line) => [line.lineId, line]))
    : null;
  const checkoutLines = args.cart.map((item) => {
    const evaluated = evaluatedByLineId?.get(item.lineId);
    const unitEffective = evaluated?.unitPrice ?? item.price;
    const qty = evaluated?.quantity ?? item.quantity;
    const lineTotal = roundMoney(unitEffective * qty, DEFAULT_MONEY_SCALE);
    return { item, evaluated, unitEffective, qty, lineTotal };
  });
  const lineSummariesForTotals: SaleDetailLineSummaryRow[] = checkoutLines.map(({ item, qty, lineTotal }) => ({
    lineId: item.lineId,
    quantity: qty,
    listTotal: lineTotal,
    discountTotal: 0,
    netTotal: lineTotal,
  }));
  const receiptTotals = receiptTotalsFromNetLines({
    lineSummaries: lineSummariesForTotals,
    merchandiseTotal: totals.total,
    tipAmount,
  });
  const handoff = buildHandoffReceiptFields({
    saleId: args.saleId,
    counterNumber: args.counterNumber,
    pickupName: args.pickupName,
    fulfillmentType: args.fulfillmentType,
    kitchenTickets: args.kitchenTickets,
    hasTable: Boolean(args.diningTableName?.trim()),
  });
  return {
    saleId: args.saleId,
    saleRef: handoff.saleRef,
    orderRefLabel: handoff.orderRefLabel,
    pickupName: handoff.pickupName,
    fulfillmentLabel: handoff.fulfillmentLabel,
    footerMessage: handoff.footerMessage,
    branchName: args.branchName,
    organizationName: args.organizationName,
    createdAt: args.createdAt ?? new Date().toISOString(),
    lines: checkoutLines.map(({ item, evaluated, qty, lineTotal }) => {
      const unitOriginal = evaluated?.unitPriceOriginal ?? item.price;
      return {
        name: item.name,
        qty,
        unitPrice: unitOriginal,
        lineTotal,
        optionsLabel: cartOptionsLabel(item),
        lineNote: item.lineNote,
        sku: item.sku,
      };
    }),
    subtotal: receiptTotals.subtotal,
    discountTotal: receiptTotals.discountTotal,
    tipAmount: tipAmount > 0 ? tipAmount : undefined,
    total: receiptTotals.total,
    payments: receiptPaymentsFromDrafts(args.paymentDrafts, args.paymentMethodNames),
    changeAmount: (() => {
      const change = checkoutChangeAmount(
        totals.totalIngresado,
        receiptTotals.total,
        args.paymentDrafts,
      );
      return change > 0 ? change : null;
    })(),
    cashierDisplayName: args.cashierDisplayName,
    customerName: args.customerName,
    diningTableName: args.diningTableName,
    footerNotes: args.footerNotes,
    trackingUrl: args.trackingUrl ?? null,
    widthMm: args.widthMm,
  };
}
